/**
 * Task mutations from `excellent/src/projects/task-actions.ts` (14 actions).
 *
 * RO-2-09c / #44416 ports the full family. Earlier slices already landed:
 *   - quickCreate + setStatus          — RO-3-00g / #44474
 *   - moveTaskToStatus + setDueDate    — RO-3-03 / #44296
 *
 * Live Next call sites stay on the original `"use server"` actions — these
 * endpoints are for the Vite client ports only (ccb89192: do not repoint live
 * Next pages at Hono).
 *
 * Path design is body-only (no `:param`) so none of this bucket can interact
 * with Hono's registration-order shadowing hazard (#44370).
 *
 * Authz: shell human-UI carve-out (setStatus / moveTaskToStatus / toggleComplete
 * are intentionally ungated on the Next server-action path). On Hono they take
 * `task:mutate` so the capability graph can still bind when engaged — same as
 * `projectActions.applyDraft`.
 */

import { z } from "zod";
import type { Operation } from "../types";

const scopeFields = {
  projectSlug: z.string().min(1),
  sectionSlug: z.string().min(1),
};

// ── create ──────────────────────────────────────────────────────────────────

const createBody = z.object({
  ...scopeFields,
  title: z.string(),
  parentId: z.number().int().positive().nullable().optional(),
  description: z.string().optional(),
});
export type TaskCreateBody = z.infer<typeof createBody>;

const createOp: Operation<void, void, TaskCreateBody, { id: number }> = {
  method: "POST",
  path: () => "/api/tasks/create",
  body: createBody,
  response: z.object({ id: z.number().int().positive() }),
};

// ── quickCreate ─────────────────────────────────────────────────────────────

const quickCreateBody = z.object({
  title: z.string(),
  projectSlug: z.string().min(1),
  sectionSlug: z.string().optional(),
  description: z.string().optional(),
  priority: z.string().optional(),
  status: z.string().optional(),
  assignee: z.string().optional(),
});
export type TaskQuickCreateBody = z.infer<typeof quickCreateBody>;

export type TaskQuickCreateResult =
  | { ok: true; id: number; projectSlug: string; sectionSlug: string }
  | { ok: false; reason: "title-required" | "project-not-found" | "no-section" };

const quickCreateOp: Operation<void, void, TaskQuickCreateBody, TaskQuickCreateResult> = {
  method: "POST",
  path: () => "/api/tasks/quick-create",
  body: quickCreateBody,
  response: z.custom<TaskQuickCreateResult>(),
};

// ── rename ──────────────────────────────────────────────────────────────────

const renameBody = z.object({
  ...scopeFields,
  taskId: z.number().int().positive(),
  title: z.string(),
});
export type TaskRenameBody = z.infer<typeof renameBody>;

const renameOp: Operation<void, void, TaskRenameBody, { ok: true }> = {
  method: "POST",
  path: () => "/api/tasks/rename",
  body: renameBody,
  response: z.object({ ok: z.literal(true) }),
};

// ── setDescription ──────────────────────────────────────────────────────────

const setDescriptionBody = z.object({
  ...scopeFields,
  taskId: z.number().int().positive(),
  description: z.string(),
});
export type TaskSetDescriptionBody = z.infer<typeof setDescriptionBody>;

const setDescriptionOp: Operation<void, void, TaskSetDescriptionBody, { ok: true }> = {
  method: "POST",
  path: () => "/api/tasks/set-description",
  body: setDescriptionBody,
  response: z.object({ ok: z.literal(true) }),
};

// ── toggleComplete ──────────────────────────────────────────────────────────

const toggleCompleteBody = z.object({
  ...scopeFields,
  taskId: z.number().int().positive(),
  done: z.boolean(),
});
export type TaskToggleCompleteBody = z.infer<typeof toggleCompleteBody>;

const toggleCompleteOp: Operation<void, void, TaskToggleCompleteBody, { ok: true }> = {
  method: "POST",
  path: () => "/api/tasks/toggle-complete",
  body: toggleCompleteBody,
  response: z.object({ ok: z.literal(true) }),
};

// ── setStatus ───────────────────────────────────────────────────────────────

const setStatusBody = z.object({
  ...scopeFields,
  taskId: z.number().int().positive(),
  status: z.string().min(1),
  /** Human's stated reason for a review decision (accept/reject) — carried into
   *  the `override.human` capture on the server; never gates the write. */
  reason: z.string().optional(),
  /**
   * §5.5 OverrideRecord.presented_as — how the item was shown to the reviewer.
   * The override-as-diff queue passes `diff`; other surfaces omit (server
   * defaults to `raw` at the seam). Never gates the write.
   */
  presented_as: z.enum(["diff", "raw"]).optional(),
  /**
   * §5.5 wall_ms — reviewer time on this decision, measured client-side from
   * presentation → click. Omit when unmeasured (queue latency is NOT a proxy).
   */
  wall_ms: z.number().int().nonnegative().optional(),
  /**
   * Epoch ms the item was presented; the recordHumanOverride seam subtracts
   * when wall_ms is omitted. Prefer wall_ms when the client already measured.
   */
  presented_at_ms: z.number().int().positive().optional(),
});
export type TaskSetStatusBody = z.infer<typeof setStatusBody>;

const setStatusOp: Operation<void, void, TaskSetStatusBody, { ok: true }> = {
  method: "POST",
  path: () => "/api/tasks/set-status",
  body: setStatusBody,
  response: z.object({ ok: z.literal(true) }),
};

// ── setPriority ─────────────────────────────────────────────────────────────

const setPriorityBody = z.object({
  ...scopeFields,
  taskId: z.number().int().positive(),
  priority: z.string(),
});
export type TaskSetPriorityBody = z.infer<typeof setPriorityBody>;

const setPriorityOp: Operation<void, void, TaskSetPriorityBody, { ok: true }> = {
  method: "POST",
  path: () => "/api/tasks/set-priority",
  body: setPriorityBody,
  response: z.object({ ok: z.literal(true) }),
};

// ── setAssignee ─────────────────────────────────────────────────────────────

const setAssigneeBody = z.object({
  ...scopeFields,
  taskId: z.number().int().positive(),
  assignee: z.string(),
});
export type TaskSetAssigneeBody = z.infer<typeof setAssigneeBody>;

const setAssigneeOp: Operation<void, void, TaskSetAssigneeBody, { ok: true }> = {
  method: "POST",
  path: () => "/api/tasks/set-assignee",
  body: setAssigneeBody,
  response: z.object({ ok: z.literal(true) }),
};

// ── setDueDate ──────────────────────────────────────────────────────────────

const setDueDateBody = z.object({
  ...scopeFields,
  taskId: z.number().int().positive(),
  dueDate: z.string().nullable(),
});
export type TaskSetDueDateBody = z.infer<typeof setDueDateBody>;

const setDueDateOp: Operation<void, void, TaskSetDueDateBody, { ok: true }> = {
  method: "POST",
  path: () => "/api/tasks/set-due-date",
  body: setDueDateBody,
  response: z.object({ ok: z.literal(true) }),
};

// ── setTaskGoal ─────────────────────────────────────────────────────────────

const setTaskGoalBody = z.object({
  ...scopeFields,
  taskId: z.number().int().positive(),
  goalId: z.number().int().positive().nullable(),
});
export type TaskSetGoalBody = z.infer<typeof setTaskGoalBody>;

const setTaskGoalOp: Operation<void, void, TaskSetGoalBody, { ok: true }> = {
  method: "POST",
  path: () => "/api/tasks/set-goal",
  body: setTaskGoalBody,
  response: z.object({ ok: z.literal(true) }),
};

// ── archive ─────────────────────────────────────────────────────────────────

const archiveBody = z.object({
  ...scopeFields,
  taskId: z.number().int().positive(),
});
export type TaskArchiveBody = z.infer<typeof archiveBody>;

const archiveOp: Operation<void, void, TaskArchiveBody, { ok: true }> = {
  method: "POST",
  path: () => "/api/tasks/archive",
  body: archiveBody,
  response: z.object({ ok: z.literal(true) }),
};

// ── reorder ─────────────────────────────────────────────────────────────────

const reorderBody = z.object({
  ...scopeFields,
  taskId: z.number().int().positive(),
  beforeTaskId: z.number().int().positive().nullable().optional(),
  afterTaskId: z.number().int().positive().nullable().optional(),
});
export type TaskReorderBody = z.infer<typeof reorderBody>;

const reorderOp: Operation<void, void, TaskReorderBody, { ok: true }> = {
  method: "POST",
  path: () => "/api/tasks/reorder",
  body: reorderBody,
  response: z.object({ ok: z.literal(true) }),
};

// ── moveTaskToProject ───────────────────────────────────────────────────────

const moveTaskToProjectBody = z.object({
  taskId: z.number().int().positive(),
  targetProjectSlug: z.string().min(1),
  targetSectionSlug: z.string().optional(),
});
export type TaskMoveToProjectBody = z.infer<typeof moveTaskToProjectBody>;

export type TaskMoveToProjectResult =
  | { ok: true; projectSlug: string; sectionSlug: string }
  | { ok: false; reason: "task-not-found" | "project-not-found" | "no-section" };

const moveTaskToProjectOp: Operation<
  void,
  void,
  TaskMoveToProjectBody,
  TaskMoveToProjectResult
> = {
  method: "POST",
  path: () => "/api/tasks/move-project",
  body: moveTaskToProjectBody,
  response: z.custom<TaskMoveToProjectResult>(),
};

// ── moveTaskToStatus ────────────────────────────────────────────────────────

const moveTaskToStatusBody = z.object({
  ...scopeFields,
  taskId: z.number().int().positive(),
  status: z.string().min(1),
  beforeTaskId: z.number().int().positive().nullable().optional(),
  afterTaskId: z.number().int().positive().nullable().optional(),
});
export type TaskMoveToStatusBody = z.infer<typeof moveTaskToStatusBody>;

const moveTaskToStatusOp: Operation<void, void, TaskMoveToStatusBody, { ok: true }> = {
  method: "POST",
  path: () => "/api/tasks/move-status",
  body: moveTaskToStatusBody,
  response: z.object({ ok: z.literal(true) }),
};

// ── export (14 — matches docs/rollover/inventory/actions.md) ────────────────

export const taskActions = {
  create: createOp,
  quickCreate: quickCreateOp,
  rename: renameOp,
  setDescription: setDescriptionOp,
  toggleComplete: toggleCompleteOp,
  setStatus: setStatusOp,
  setPriority: setPriorityOp,
  setAssignee: setAssigneeOp,
  setDueDate: setDueDateOp,
  setTaskGoal: setTaskGoalOp,
  archive: archiveOp,
  reorder: reorderOp,
  moveTaskToProject: moveTaskToProjectOp,
  moveTaskToStatus: moveTaskToStatusOp,
};
